import { Box, CircularProgress, Divider, IconButton, TextField, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import QrCodeStylingComponent from '../FormBuilder/styledQR';
import { CopyIcon } from '../icons';

const customStyles = {
  mainHeading: {
    fontFamily: 'Noto Sans',
    fontWeight: 500,
    fontStyle: 'Medium',
    fontSize: '24px',
    lineHeight: '32px',
    letterSpacing: '0%',
    textAlign: 'center',
    '@media (max-width: 600px)': {
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  description: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'Regular',
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0%',
    textAlign: 'center',
  },
  qrContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  qrCode: {
    width: '218px',
    height: '218px',
  },
  manualEntryContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    padding: '0px',
    gap: '8px',
  },
  divider: {
    position: 'relative',
    height: '2px',
    backgroundColor: '#DDE0E5',
    borderWidth: '0px',
    flex: 1,
  },
  dividerText: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'Regular',
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0%',
    textAlign: 'center',
  },
  secretCodeInput: {
    '& .MuiOutlinedInput-root': {
      width: '100%',
      height: '30px',
      borderRadius: '8px',
      padding: '5px 12px',
      background: '#FAFBFF',
      '& fieldset': {
        borderColor: '#FAFBFF',
      },
      '&:hover fieldset': {
        borderColor: '#FAFBFF',
      },
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Noto Sans',
      fontWeight: 400,
      fontStyle: 'Regular',
      fontSize: '14px',
      lineHeight: '22px',
      letterSpacing: '0%',
      padding: '0px',
    },
  },
  qrStepContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
};

interface MfaQrProps {
  qrData: string;
  isLoading: boolean;
  secretCode: string;
  handleCopyCode: () => void;
}

export const MfaQr = ({ qrData, isLoading, secretCode, handleCopyCode }: MfaQrProps) => {
  const lang = useLanguage();
  return (
    <Box sx={customStyles.qrStepContainer}>
      <Typography sx={customStyles.mainHeading}>{lang.mfa.scanQr}</Typography>
      <Typography sx={customStyles.description}>{lang.mfa.scanQrDescription}</Typography>

      <Box sx={customStyles.qrContainer}>
        {isLoading ? (
          <CircularProgress size={200} />
        ) : qrData ? (
          <Box sx={customStyles.qrCode}>
            <QrCodeStylingComponent
              data={qrData}
              qrStyles={{
                width: 218,
                height: 218,
                margin: 0,
              }}
            />
          </Box>
        ) : null}
      </Box>

      <Box sx={customStyles.manualEntryContainer}>
        <Divider sx={customStyles.divider} />
        <Typography sx={customStyles.dividerText}>{lang.mfa.manualEntry}</Typography>
        <Divider sx={customStyles.divider} />
      </Box>
      <TextField
        fullWidth
        value={secretCode}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <IconButton
              size='small'
              onClick={handleCopyCode}
            >
              <CopyIcon
                fontSize='inherit'
                color='inherit'
              />
            </IconButton>
          ),
        }}
        sx={customStyles.secretCodeInput}
      />
    </Box>
  );
};
